import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getEventDetails } from "../service/api/eventService";
import { Spinner } from "react-bootstrap";
import { formatAmountInThousands, formatDate } from "../service/utils/formatters";
import { formatAmount } from "../service/utils/orderUtils";
import "./style/EventDetailsModal.css"; // Fichier CSS dédié pour les styles avancés
import { formatCurrency } from '../service/utils/facturationUtils';

// =============================================================================
// COMPOSANTS D'INTERFACE RÉUTILISABLES
// =============================================================================

/**
 * Composant d'en-tête de section avec icône
 */
const SectionHeader = ({ title, icon, className = "" }) => (
  <div className={`section-header ${className}`}>
    <i className={`fas fa-${icon} me-2`}></i>
    <h6 className="section-title mb-0">{title}</h6>
  </div>
);

/**
 * Composant de carte d'information avec mise en forme élégante
 */
const InfoCard = ({ label, value, badge, icon, className = "" }) => (
  <div className={`info-card ${className}`}>
    <div className="info-card-header">
      {icon && <i className={`fas fa-${icon} me-2`}></i>}
      <span className="info-card-label">{label}</span>
    </div>
    <div className="info-card-value mt-5">
      {badge ? (
        <span className={`badge ${badge.className}`}>
          {badge.icon && <i className={`fas fa-${badge.icon} me-1`}></i>}
          {value}
        </span>
      ) : (
        <span>{value || "-"}</span>
      )}
    </div>
  </div>
);

/**
 * Composant de tableau amélioré avec en-tête stylisé
 */
const EnhancedTable = ({ title, data, columns, emptyMessage = "Aucune donnée disponible" }) => (
  <div className="enhanced-table-container">
    {title && <SectionHeader title={title} icon="table" className="mb-3" />}
    
    <div className="table-responsive">
      <table className="table table-hover align-middle table-striped">
        <thead className="table-light">
          <tr>
            {columns.map((column, index) => (
              <th key={index} className={column.className || ""}>
                {column.icon && <i className={`fas fa-${column.icon} me-2`}></i>}
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data && data.length > 0 ? (
            data.map((row, rowIndex) => (
              <tr key={rowIndex} className="table-row-hover">
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className={column.cellClassName || ""}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center py-4 text-muted">
                <i className="fas fa-inbox me-2"></i>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Composant de statut avec indicateur visuel
 */
const StatusIndicator = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      "Pending": { className: "status-pending", icon: "clock" },
      "Processed": { className: "status-completed", icon: "check-circle" },
      "Cancelled": { className: "status-cancelled", icon: "x-circle" },
      "In Progress": { className: "status-progress", icon: "arrow-repeat" }
    };
    return configs[status] || { className: "status-unknown", icon: "question-circle" };
  };

  const config = getStatusConfig(status);
  
  return (
    <span className={`status-indicator ${config.className}`}>
      <i className={`fas fa-${config.icon} me-1`}></i>
      {status}
    </span>
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL - MODAL DE DÉTAILS D'ÉVÉNEMENT
// =============================================================================

/**
 * Modal de détails d'événement avec interface professionnelle et moderne
 * Affiche les informations détaillées d'un événement OST avec une mise en forme élégante
 */
const EventDetailsModal = ({ eventId, onClose }) => {
  // ===========================================================================
  // ÉTATS ET HOOKS
  // ===========================================================================
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ===========================================================================
  // EFFETS
  // ===========================================================================

  /**
   * Récupération des détails de l'événement
   */
  useEffect(() => {
    async function fetchDetails() {
      if (!eventId) return;
      
      try {
        setLoading(true);
        setError(null);
        const res = await getEventDetails(eventId);
        setDetails(res.data);
      } catch {
        // console.error("Erreur lors du chargement des détails:", err);
        // setError("Impossible de charger les détails de l'événement. Veuillez réessayer.");
        toast.error("Desole, une erreur s'est produite lors de la récupération des détails de l'événement.");
      } finally {
        setLoading(false);
      }
    }

    fetchDetails();
  }, [eventId]);

  // ===========================================================================
  // CONFIGURATIONS DES TABLEAUX
  // ===========================================================================

  // Colonnes pour le tableau principal des informations
  const mainInfoColumns = [
    {
      key: "label",
      header: "Information",
      className: "fw-semibold text-uppercase small text-muted",
      cellClassName: "py-3"
    },
    {
      key: "value",
      header: "Valeur",
      className: "fw-semibold text-uppercase small text-muted",
      cellClassName: "py-3"
    }
  ];

  // Colonnes pour le tableau des comptes titres
  const accountColumns = [
    {
      key: "lastName",
      header: "Nom du client",
      icon: "person",
      className: "fw-semibold"
    },
    {
      key: "email",
      header: "Email",
      icon: "envelope",
      className: "fw-semibold"
    },
    {
      key: "quantity",
      header: "Quantité détenue",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {row.quantity.toLocaleString()}
        </span>
      )
    },
    {
      key: "ancienneQuantite",
      header: "Quantité ancien",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {row.ancienneQuantite.toLocaleString()}
        </span>
      )
    },
    {
      key: "nouvelleQuantite",
      header: "Quantité nouvelle",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {row.nouvelleQuantite.toLocaleString()}
        </span>
      )
    },
    {
      key: "montantCash",
      header: "Montant cash",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {formatCurrency(row.montantCash)}
        </span>
      )
    },
    {
      key: "ancienCash",
      header: "cash ancien",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {formatCurrency(row.ancienCash)}
        </span>
      )
    },
    {
      key: "nouveauCash",
      header: "Nouveau cash",
      icon: "pie-chart",
      className: "fw-semibold text-end",
      cellClassName: "text-end",
      render: (row) => (
        <span className="quantity-badge">
          {formatCurrency(row.nouveauCash)}
        </span>
      )
    }
  ];

  // Données transformées pour le tableau principal
  const mainInfoData = details ? [
    {
      label: "Type d'événement",
      value: details.type || "-"
    },
    {
      label: "Montant",
      value: (
        <span className="amount-display">
          {formatAmountInThousands(details.amount)}
        </span>
      )
    },
    {
      label: "Nombre de Titre a distribuer",
      value: (
          <span className="amount-display">
            {formatAmount(details.nombreTitre)}
          </span>
      )
    },
    {
      label: "Statut",
      value: <StatusIndicator status={details.status} />
    },
    {
      label: "Date de planification",
      value: details.dateDePlanification ? formatDate(details.dateDePlanification) : "-"
    },
    {
      label: "Date de réalisation",
      value: details.dateDeRealisation ? formatDate(details.dateDeRealisation) : "-"
    },
    {
      label: "Type de marché",
      value: details.targetMarket || "-"
    },
    {
      label: "Nombre de comptes impactés",
      value: (
        <span className="account-count">
          {details.accountCount || 0}
        </span>
      )
    }
  ] : [];

  // ===========================================================================
  // RENDU PRINCIPAL
  // ===========================================================================

  return (
    <div className="modal fade show d-block event-details-modal" tabIndex="-1" aria-modal="true" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content shadow-lg">
          
          {/* En-tête élégant avec dégradé */}
          <div className="modal-header gradient-header">
            <div className="header-content">
              <i className="fas fa-calendar-event me-3"></i>
              <div>
                <h5 className="modal-title mb-0">Détails de l'événement</h5>
                {/* <small className="text-white-50">Référence #{eventId}</small> */}
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              aria-label="Fermer"
            ></button>
          </div>

          {/* Corps de la modale */}
          <div className="modal-body">
            {loading ? (
              // État de chargement élégant
              <div className="loading-state text-center py-5">
                <div className="spinner-container mb-3">
                  <Spinner
                    animation="border"
                    role="status"
                    variant="primary"
                    className="custom-spinner"
                  />
                </div>
                <h6 className="text-muted mb-2">Chargement des détails...</h6>
                <p className="text-muted small">
                  Récupération des informations de l'événement
                </p>
              </div>
            ) : error ? (
              // État d'erreur
              <div className="error-state text-center py-5">
                <i className="fas fa-exclamation-triangle text-danger display-4 mb-3"></i>
                <h6 className="text-danger mb-2">Erreur de chargement</h6>
                <p className="text-muted mb-4">{error}</p>
                <button 
                  className="btn btn-outline-primary"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-arrow-clockwise me-2"></i>
                  Réessayer
                </button>
              </div>
            ) : details ? (
              // Contenu principal des détails
              <div className="details-content">
                
                {/* Section des informations principales */}
                <section className="main-info-section mb-4">
                  <SectionHeader 
                    title="Informations principales" 
                    icon="info-circle"
                  />
                  <EnhancedTable
                    data={mainInfoData}
                    columns={mainInfoColumns}
                    className="main-info-table"
                  />
                </section>

                {/* Section des comptes titres */}
                {details.accountSecurityInfos && details.accountSecurityInfos.length > 0 && (
                  <section className="accounts-section">
                    <SectionHeader 
                      title={`Comptes détenant le titre (${details.accountSecurityInfos.length})`} 
                      icon="people"
                    />
                    <EnhancedTable
                      data={details.accountSecurityInfos}
                      columns={accountColumns}
                      emptyMessage="Aucun compte trouvé pour ce titre"
                    />
                  </section>
                )}

                {/* Informations supplémentaires */}
                <section className="additional-info mt-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <InfoCard
                        label="Capital"
                        value={details.capital ? `${(details.capital)}%` : "Non spécifié"}
                        icon="currency-dollar"
                        className={!details.capital ? "text-muted" : ""}
                      />
                    </div>
                    <div className="col-md-6">
                      <InfoCard
                        label="Impôt"
                        value={details.impot ? `${formatAmount(details.impot)} Fcfa` : "Non applicable"}
                        icon="receipt"
                        className={!details.impot ? "text-muted" : ""}
                      />
                    </div>
                  </div>
                </section>

              </div>
            ) : null}
          </div>

          {/* Pied de page avec actions */}
          <div className="modal-footer border-top-0">
            <button 
              className="btn btn-outline-secondary me-2"
              onClick={onClose}
            >
              <i className="fas fa-x-circle me-2"></i>
              Fermer
            </button>
            {/* <button 
              className="btn btn-primary"
              onClick={() => {
                // Action d'export ou autre fonctionnalité
                toast.success("Fonctionnalité d'export en cours de développement");
              }}
            >
              <i className="fas fa-download me-2"></i>
              Exporter les détails
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;